import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate} from 'react-router-dom';
import { setSelectedMenuItem } from '../slicers/sidebarSlice';
import { EmptyCalendar, UserProfilePictureIcon } from './icons/Icons';
import styles from './PatientAppointment.module.css';
import { setShowDetail, setSelectedAppointment } from '../views/appointment/AppointmentDetailSlicer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/free-solid-svg-icons';

const PatientAppointment = ({ appointments }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentMeetings = useSelector((state) => state.appointment.currentMeetings).flat();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for real-time appointment timing
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  const calculateMinutesToMeeting = (appointmentStartDateTime) => {
    const appointmentStart = new Date(appointmentStartDateTime);

    const timeDifference = (appointmentStart - currentTime) / 1000 / 60; // difference in minutes
    const minutesRounded = Math.ceil(timeDifference);

    if (minutesRounded === 0 && timeDifference > -1) {
      return 'Now'
    } else if (timeDifference > 0 && timeDifference <= 30) {
      return `in ${minutesRounded} min${minutesRounded !== 1 ? "s" : ""}`;
    } else if (timeDifference < 0){
      const overdueMinutes = Math.abs(minutesRounded);
      return `overdue by ${overdueMinutes} min${overdueMinutes !== 1 ? "s" : ""}`;
    } else {
      return null;
    }
  };

  const formatDate = (date) => {
    const givenDateUTC = new Date(date);
    const givenDate = new Date(givenDateUTC.getTime() - givenDateUTC.getTimezoneOffset() * 60000); 
    const today = new Date();
    const localToday = new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const appointmentDate = givenDate.toISOString().slice(0, 10);

    if (appointmentDate === localToday) {
      return "";
    } else {
      const month = String(givenDate.getMonth() + 1).padStart(2, '0');
      const day = String(givenDate.getDate()).padStart(2, '0');
      return `${month}/${day}`;
    }
  };

  const formatTimeToLocal = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const sortedAppointments = (appointments || [])
  .filter(appointment => {
    // Show appointments that haven't ended yet
    const appointmentEnd = new Date(appointment.appointmentEndDateTime);
    return appointmentEnd > currentTime;
  })
  .sort((a, b) => new Date(a.appointmentStartDateTime) - new Date(b.appointmentStartDateTime));

  const handleAppointmentClick = (appointment) => {
    dispatch(setSelectedMenuItem('Appointments'));
    dispatch(setShowDetail(true));
    dispatch(setSelectedAppointment(appointment));
    navigate("/patient-appointment");
  };
  
  return (
    <div className={styles.patientAppointmentContainer}>
      {sortedAppointments.length === 0 ?
        <div className={styles.noAppointments}>
          <EmptyCalendar />
          <div>{`No Appointments`}</div>
        </div>
        : (
        sortedAppointments.slice(0,5).map((appointment, index) => {
          let appointmentOverdue = new Date(appointment.appointmentStartDateTime) < currentTime;
          return (
          <div key={index} className={styles.mainContainer} onClick={()=>handleAppointmentClick(appointment)}>
            <div className={styles.innerContainer}>
              <div className={styles.icon}>
                <span className={styles.initials}>
                  {(() => {
                    const names = appointment?.patientName?.split(' ') || [];
                    const first = names[0]?.[0] || '';
                    const second = names[1]?.[0] || '';
                    return `${first}${second}`;
                  })()}
                </span>
                <UserProfilePictureIcon width="28" height="28"/>
              </div>
              <div className={styles.userName}>{appointment.patientName}</div>
            </div>
            <div className={styles.textContainer}>
              <div className={styles.timestamp}>{formatDate(appointment.appointmentStartDateTime)}</div>
              {(() => {
                // Check if appointment is in currentMeetings (patient in waiting room)
                // Only use currentMeetings for real-time state - this is updated via WebSocket
                const isInCurrentMeetings = currentMeetings.some(
                  (meeting) => meeting.appointmentId === appointment.appointmentId
                );
                // Show "In Waiting Room" ONLY if it's in currentMeetings (real-time WebSocket state)
                // Don't rely on appointment.isActive as it may be stale from backend
                const isInWaitingRoom = isInCurrentMeetings;
                return isInWaitingRoom ? (
                  <div className={styles.inWaitingRoom}>
                    <FontAwesomeIcon icon={faVideo} color={"white"}/>
                    In Waiting Room
                  </div>
                ) : (
                  <>
                    <div className={styles.additionalText} style={{color: "#9F1919"}} >{calculateMinutesToMeeting(appointment.appointmentStartDateTime)}</div>
                    <div className={styles.appointmentTimeContainer} style={appointmentOverdue ? { background: "rgba(235, 182, 182, 0.80)", border:"0.5px solid #630F0F" } : { background: "#F3F3F3", border:"0.5px solid #828282" }} >
                      <p className={styles.appointmentTime}>{formatTimeToLocal(appointment.appointmentStartDateTime)}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )})
        )
      }
    </div>

  );
};

export default PatientAppointment;
